import { Service, inject } from '@angular/core';

import { Observable, from, of, switchMap, map, take, catchError } from 'rxjs';

import { ApolloClientService } from 'src/app/services/apollo-client.service';
import { SOPS, SOP_OPTIONS, TEST_PERIODS, CONFIGURATOR_USER_FUNCTIONS } from 'src/app/graphql/queries/treo-reference-data.queries';
import { Sop } from 'src/app/models/sop';
import { TestPeriod } from 'src/app/models/test-period';
import { UfNode } from 'src/app/models/uf-uc-scn-tree';
import { GraphQLSopOutput, GraphQLTestPeriodOutput, GraphQLUserFunctionOutput } from 'src/app/graphql/models/output/treo-reference-data.output.model';
import { convertGraphQLSopsToDto, convertGraphQLTestPeriodsToDto, convertGraphQLUserFunctionsToDto } from 'src/app/graphql/mappers/treo-reference-data.mapper';

/**
 * TREO reference/lookup data from the abstraction layer (the same GraphQL endpoint apolloClient$
 * already targets): SOPs, test periods (test weeks), and the UF/UC/SCN structure. Used to populate
 * dropdowns/options across the vehicle manual-input grid and the configurations/report export
 * dialogs. Fetches the raw responses and delegates all shaping (filtering, sorting, view-model
 * mapping) to treo-reference-data.mapper. Mirrors how ECU Compare loads SOPs.
 */
@Service()
export class TreoReferenceDataService {
	private apolloClientService = inject(ApolloClientService);

	/**
	 * Loads the SOP dropdown options. See convertGraphQLSopsToDto for the shaping rules ("Main" =
	 * latest SOP, version-0 numeric names, deduped, newest-first). Returns [] on error.
	 */
	public getSops$(): Observable<Sop[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				from(
					client.query<{ sops: (GraphQLSopOutput | null)[] | null }>({
						query: SOPS,
						fetchPolicy: 'no-cache',
					}),
				),
			),
			map(result => convertGraphQLSopsToDto(result.data?.sops ?? null)),
			catchError((err: any) => {
				console.error('[TreoReferenceDataService] Failed to load SOPs:', err);
				return of([] as Sop[]);
			}),
		);
	}

	/**
	 * Loads the test-period (test week) dropdown options. See convertGraphQLTestPeriodsToDto for the
	 * shaping rules (version-0 with a name, newest-first by validFrom). Returns [] on error.
	 */
	public getTestPeriods$(): Observable<TestPeriod[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				from(
					client.query<{ testPeriods: (GraphQLTestPeriodOutput | null)[] | null }>({
						query: TEST_PERIODS,
						variables: { sortByName: true },
						fetchPolicy: 'no-cache',
					}),
				),
			),
			map(result => convertGraphQLTestPeriodsToDto(result.data?.testPeriods ?? null)),
			catchError((err: any) => {
				console.error('[TreoReferenceDataService] Failed to load test periods:', err);
				return of([] as TestPeriod[]);
			}),
		);
	}

	/**
	 * SOP option names for the report / configurations dialogs, with "Main" last: the distinct SOPs
	 * that actually appear on imported main-suite documents (the analyzerSopOptions backend query —
	 * TREO's /api/sop equivalent), latest `limit`, ordered oldest→newest so the newest sits at
	 * the right, then a synthetic "Main" appended. Unlike the SOP master list (getSops), this excludes
	 * future/unused SOPs that have no results. Returns [] on error.
	 * @param analyzerEnvironment
	 * @param limit
	 */
	public getSopOptionsMainLast$(analyzerEnvironment: string, limit: number): Observable<string[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				from(
					client.query<{ analyzerSopOptions: string[] | null }>({
						query: SOP_OPTIONS,
						variables: { analyzerEnvironment, limit },
						fetchPolicy: 'network-only',
					}),
				),
			),
			// Backend returns the SOPs ascending (oldest→newest); just append the synthetic "Main".
			map(result => [...(result.data?.analyzerSopOptions ?? []), 'Main']),
			catchError((err: any) => {
				console.error('[TreoReferenceDataService] Failed to load report SOP options:', err);
				return of([] as string[]);
			}),
		);
	}

	/**
	 * Recent test-week option names, ordered oldest→newest and anchored on the current test week: the
	 * week whose validity window contains now (validFrom ≤ now ≤ validTo, the same rule the
	 * manual-input grid uses to preselect) is treated as the latest, and the `limit` weeks up to and
	 * including it are returned. If no week is current, the newest `limit` are used. Returns [] on error.
	 * @param limit
	 */
	public getRecentTestWeekOptions$(limit: number): Observable<string[]> {
		return this.getTestPeriods$().pipe(
			map(periods => {
				// getTestPeriods returns newest-first; flip to oldest→newest for indexing.
				const oldestFirst = [...periods].reverse();
				const now = Date.now();
				let anchorIdx = oldestFirst.findIndex(tw => {
					const from = tw.validFrom?.getTime();
					const to = tw.validTo?.getTime();
					return from != null && to != null && from <= now && now <= to;
				});
				if (anchorIdx < 0) {
					anchorIdx = oldestFirst.length - 1; // no current week → anchor on the newest
				}
				const start = Math.max(0, anchorIdx - (limit - 1));
				return oldestFirst.slice(start, anchorIdx + 1).map(t => t.name);
			}),
		);
	}

	/**
	 * Loads the active UF → UC → SCN tree (already nested by the backend), for the cascading
	 * dropdowns. Not SOP-scoped — configuratorUserFunctions returns all active items. See
	 * convertGraphQLUserFunctionsToDto for the shaping. Returns [] on error.
	 */
	public getUfTree$(): Observable<UfNode[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				from(
					client.query<{ configuratorUserFunctions: (GraphQLUserFunctionOutput | null)[] | null }>({
						query: CONFIGURATOR_USER_FUNCTIONS,
						fetchPolicy: 'no-cache',
					}),
				),
			),
			map(result => convertGraphQLUserFunctionsToDto(result.data?.configuratorUserFunctions ?? null)),
			catchError((err: any) => {
				console.error('[TreoReferenceDataService] Failed to load UF tree:', err);
				return of([] as UfNode[]);
			}),
		);
	}
}
